"use client";

import Footer from "../ui/Footer/Footer";
import Slide from "../ui/Slide/Slide";
import Link from "next/link";
import Button from "../ui/Button/Button";
import Image from "next/image";
import { useCallback, useEffect, useState, useRef } from "react";
import autosize from "autosize";
import { getVimeoId } from "@/helpers/vimeoId";

import { sha256 } from "js-sha256";
import NewHeader from "../ui/Header/NewHeader/NewHeader";
import Vimeo from "@u-wave/react-vimeo";
import UserLayout from "../ui/UserLayout/UserLayout";
import moment from "moment-timezone";
import { toast } from "react-toastify";
import { useRouter, useSearchParams } from "next/navigation";

export default function Dashboard(props) {
    // Refs
    const router = useRouter();
    const session = props.session;
    const searchParams = useSearchParams();
    moment.locale("fr");
    const addPostTextareaRef = useRef(null);
    const highlightedVideoRef = useRef(null);
    const observerRef = useRef(null);

    // States
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingDashboard, setIsLoadingDashboard] = useState(false);
    const [articles, setArticles] = useState([]);
    const [allCourses, setAllCourses] = useState([]);
    const [mentors, setMentors] = useState([]);
    const [userCourses, setUserCourses] = useState([]);
    const [userPrograms, setUserPrograms] = useState([]);
    const [certifications, setCertifications] = useState([]);
    const [progressions, setProgressions] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [subsPrograms, setSubsPrograms] = useState([]);
    const [all, setAll] = useState(false);
    const [activities, setActivities] = useState([]);
    const [filterCategory, setFilterCategory] = useState("all");
    const [calls, setCalls] = useState([]);
    const [events, setEvents] = useState([]);

    // New states
    const [highlightedEpisode, setHighlightedEpisode] = useState(null);
    const [episodesToHighlight, setEpisodesToHighlight] = useState([]);
    const [highlightedEpisodeIsMuted, setHighlightedEpisodeIsMuted] =
        useState(true);
    const [highlightedEpisodeIsPaused, setHighlightedEpisodeIsPaused] =
        useState(false);
    const [lastCourses, setLastCourses] = useState([]);
    const [allEventsPlanned, setAllEventsPlanned] = useState([]);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [modal, setModal] = useState(null);
    const [videoIsForcedPaused, setVideoIsForcedPaused] = useState(false);
    const [series, setSeries] = useState([]);
    const [highlight, setHighlight] = useState(null);
    const [video, setVideo] = useState(null);
    const [highlightEnd, setHighlightEnd] = useState(false);
    const [videoIsLoading, setVideoIsLoading] = useState(true);
    const [refreshVideo, setRefreshVideo] = useState(false);
    const [conversionSend, setConversionSend] = useState(false);

    const loadDashboard = async () => {
        if (!isLoadingDashboard) {
            setIsLoadingDashboard(true);

            const response = await fetch("/api/user/get/dashboard", {
                method: "get",
                headers: {
                    "Content-Type": "application/json",
                },
            });

            const fetchedData = await response.json();

            if (!response.ok) {
                setIsLoadingDashboard(false);
                toast.error(fetchedData.message);
            } else {
                setUserCourses(fetchedData.userCourses);
                setUserPrograms(fetchedData.userPrograms);
                setCertifications(fetchedData.certifications);
                setProgressions(fetchedData.progressions);
                setTasks(fetchedData.tasks);
                setSubsPrograms(fetchedData.subsPrograms);
                setActivities(fetchedData.activities);
                setFilterCategory(fetchedData.filterCategory);
                setCalls(fetchedData.calls);
                setLastCourses(fetchedData.lastCourses);
                setSeries(fetchedData.series);
                let tempEvents = [];
                fetchedData.events.forEach((event) => {
                    if (!event.eventDate.isEnded) {
                        tempEvents.push(event);
                    }
                });
                setEvents(tempEvents);
                setMentors(fetchedData.mentors);
                const tempPlanned = [...tempEvents].concat(fetchedData.calls);
                setEpisodesToHighlight(fetchedData.highlights);
                setHighlightedEpisode(
                    fetchedData.highlights.length != 0
                        ? fetchedData.highlights[0]
                        : null,
                );

                // Sort dates moment
                tempPlanned.sort((a, b) => {
                    return (
                        moment(
                            a.eventDate ? a.eventDate.date : a.plannedDate,
                        ).unix() -
                        moment(
                            b.eventDate ? b.eventDate.date : b.plannedDate,
                        ).unix()
                    );
                });
                setAllEventsPlanned(tempPlanned);
                // Take userCourses and userPrograms and subsPrograms and put them in one array
                let allCourses = [];
                fetchedData.userCourses.forEach((course) => {
                    allCourses.push(course);
                });
                fetchedData.userPrograms.forEach((program) => {
                    allCourses.push(program);
                });
                fetchedData.subsPrograms.forEach((program) => {
                    allCourses.push(program);
                });

                // Order object dictionary fetchedData.progressions
                let orderedProgressions = [];
                Object.keys(fetchedData.progressions)
                    .sort()
                    .forEach(function (key) {
                        fetchedData.progressions[key]["id"] = key;
                        orderedProgressions[key] =
                            fetchedData.progressions[key];
                    });
                // orderedProgressions by date
                let orderedProgressionsDate = orderedProgressions
                    .filter((date) => date["last"] !== null)
                    .sort(
                        (a, b) =>
                            new Date(a["last"]["date"]) -
                            new Date(b["last"]["date"]),
                    )
                    .reverse();
                orderedProgressions = [
                    ...orderedProgressionsDate,
                    ...orderedProgressions.filter(
                        (progression) => progression["last"] === null,
                    ),
                ];
                let allCoursesOrdered = [];
                orderedProgressions.forEach((progression) => {
                    allCourses.forEach((course) => {
                        if (course.product.id == progression.id) {
                            course.progression = progression;
                            allCoursesOrdered.push(course);
                        }
                    });
                });
                setAllCourses(allCoursesOrdered);
                if (searchParams.get("firstTimeMentor")) {
                    router.push("/mentor");
                    return;
                }
                setIsLoadingDashboard(false);
            }
        }
    };
    const loadArticles = async () => {
        if (!isLoading) {
            setIsLoading(true);

            const response = await fetch("/api/blog/get/articles", {
                method: "get",
                headers: {
                    "Content-Type": "application/json",
                },
            });

            const fetchedData = await response.json();

            if (!response.ok) {
                setIsLoading(false);
                toast.error(fetchedData.message);
            } else {
                setArticles(fetchedData.articles);
                setIsLoading(false);
            }
        }
    };
    const onSetHighlightedEpisodeClicked = (episode) => {
        setHighlightedEpisode(episode);
        setHighlightedEpisodeIsMuted(false);
        setHighlightedEpisodeIsPaused(false);
        setVideoIsForcedPaused(false);
        setHighlightEnd(false);
    };
    const newMetaEventLog = async () => {
        // Facebook API Conversion
        let userIp;
        await fetch("https://api.ipify.org?format=json")
            .then((response) => response.json())
            .then((data) => {
                userIp = data.ip;
            });
        await fetch(
            `https://graph.facebook.com/v19.0/${process.env.FACEBOOK_PIXELID}/events?access_token=${process.env.FACEBOOK_PIXELTOKEN}`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    data: [
                        {
                            event_name: "ViewContent",
                            event_time: Math.floor(Date.now() / 1000),
                            event_id: `${moment().unix()}-${Math.floor(
                                Math.random() * 1000000000,
                            )}`,
                            event_source_url: `https://believemy.com`,
                            action_source: "website",
                            user_data: {
                                client_ip_address: userIp || "0.0.0.0",
                                client_user_agent: navigator.userAgent,
                                em:
                                    session && session.user
                                        ? [sha256(session.user.email)]
                                        : "",
                                fn:
                                    session && session.user
                                        ? [sha256(session.user.firstName)]
                                        : "",

                                ln:
                                    session && session.user
                                        ? [sha256(session.user.lastName)]
                                        : "",

                                fbc: "fb.1.1554763741205.AbCdEfGhIjKlMnOpQrStUvWxYz1234567890",
                                fbp: "fb.1.1558571054389.1098115397",
                            },
                            custom_data: {
                                // value: router.query.subscription_price,
                                // currency: "EUR",
                                content_ids: ["home"],
                                content_name: "Home",
                                // content_type: "product",
                            },
                            // opt_out: false,
                        },
                    ],
                }),
            },
        );
    };

    autosize(addPostTextareaRef.current);

    useEffect(() => {
        if (props.user) {
            loadDashboard();
            loadArticles();
            // Timer 3sec
            const timer = setTimeout(() => {
                setRefreshVideo(true);
            }, 3000);
            return () => clearTimeout(timer);
        } else {
            loadArticles();
        }
    }, []);

    useEffect(() => {
        if (props.user) {
            let observer = false;
            const videoElement = highlightedVideoRef.current;
            const options = {
                rootMargin: "0px",
                threshold: 0.4, // 10% de la vidéo visible
            };

            if (videoElement != null) {
                const handleIntersection = (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            // La vidéo est visible
                            if (!videoIsForcedPaused) {
                                // videoElement.play();
                                setHighlightedEpisodeIsPaused(false);
                            }
                        } else {
                            // La vidéo n'est plus visible
                            // videoElement.pause();
                            setHighlightedEpisodeIsPaused(true);
                        }
                    });
                };

                observer = new IntersectionObserver(
                    handleIntersection,
                    options,
                );

                observer.observe(videoElement.player.element);

                observerRef.current = observer;
            }
            return () => {
                if (observerRef && observerRef.current) {
                    observerRef.current.unobserve(videoElement.player.element);
                }
            };
        }
    }, [
        highlightedEpisode,
        highlightedVideoRef,
        videoIsForcedPaused,
        refreshVideo,
    ]);

    useEffect(() => {
        if (!modalIsOpen && modal) {
            window.history.replaceState(
                null,
                "",
                `${process.env.NEXTAUTH_URL}`,
            );
        }
    }, [modalIsOpen]);

    useEffect(() => {
        if (!highlightedEpisode) return;
        const temp = {
            type: highlightedEpisode.type,
        };
        if (highlightedEpisode.type == "episode") {
            temp.video = highlightedEpisode.episode.video;
            temp.title = highlightedEpisode.episode.title;
            temp.imageTitle = highlightedEpisode.episode.season.serie.imageTitle
                ? highlightedEpisode.episode.season.serie.imageTitle
                : null;
            temp.description = highlightedEpisode.episode.description;
            temp.thumbnail = highlightedEpisode.episode.thumbnail;
            temp.isBelievemyOriginals =
                highlightedEpisode.episode.isBelievemyOriginals;
            temp.duration = highlightedEpisode.episode.duration;
            temp.language = highlightedEpisode.episode.language;
            temp.subtitles = highlightedEpisode.episode.subtitles;
            temp.storyArt = highlightedEpisode.episode.storyArt
                ? highlightedEpisode.episode.storyArt
                : null;
            temp.link = `/series/${highlightedEpisode.episode.season.serie.slug}/${highlightedEpisode.episode.season.position}/${highlightedEpisode.episode.position}`;
        }

        if (highlightedEpisode.type == "course") {
            temp.video = highlightedEpisode.course.video;
            temp.title = highlightedEpisode.course.name;
            temp.imageTitle = highlightedEpisode.course.imageTitle
                ? highlightedEpisode.course.imageTitle
                : null;
            temp.description = highlightedEpisode.course.summary;
            temp.thumbnail = highlightedEpisode.course.storyArt;
            temp.isBelievemyOriginals =
                highlightedEpisode.course.isBelievemyOriginals;
            temp.duration = highlightedEpisode.course.duration;
            temp.language = highlightedEpisode.course.language;
            temp.subtitles = highlightedEpisode.course.subtitles;
            temp.storyArt = highlightedEpisode.course.storyArt
                ? highlightedEpisode.course.storyArt
                : null;
            temp.link = `/course/${highlightedEpisode.course.slug}`;
        }

        if (highlightedEpisode.type == "announcement") {
            temp.video = highlightedEpisode.announcement.video;
            temp.title = highlightedEpisode.announcement.title;
            temp.imageTitle = highlightedEpisode.announcement.imageTitle
                ? highlightedEpisode.announcement.imageTitle
                : null;
            temp.description = highlightedEpisode.announcement.description;
            temp.thumbnail = highlightedEpisode.announcement.thumbnail;
            temp.isBelievemyOriginals = false;
            // temp.duration = highlightedEpisode.course.duration;
            // temp.language = highlightedEpisode.course.language;
            // temp.subtitles = highlightedEpisode.course.subtitles;
            temp.storyArt = highlightedEpisode.announcement.storyArt
                ? highlightedEpisode.announcement.storyArt
                : null;
            temp.link = highlightedEpisode.announcement.redirectLink ?? "";
            // Video, storyArt
        }
        setVideoIsLoading(true);
        setHighlight(temp);
    }, [highlightedEpisode]);

    // Callbacks
    const restartVideo = useCallback(() => {
        video.setCurrentTime(0);
    }, [video]);

    let controls = (
        <>
            {/* Mute */}
            <div
                className="rounded-full bg-white bg-opacity-50 p-3 cursor-pointer hover:bg-opacity-100 group duration-150"
                onClick={() =>
                    setHighlightedEpisodeIsMuted(!highlightedEpisodeIsMuted)
                }
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-white group-hover:text-gold duration-150"
                    viewBox="0 0 24 24"
                >
                    {highlightedEpisodeIsMuted ? (
                        <path
                            fill="currentColor"
                            fillRule="evenodd"
                            d="M12.14 4.91c-.714.347-1.62.942-2.909 1.792l-.219.144l-.05.033c-.38.25-.643.424-.931.552a3.75 3.75 0 0 1-.89.267c-.31.052-.626.052-1.082.052H6c-1.444 0-1.93.016-2.345.208a2.47 2.47 0 0 0-1.036.975c-.218.405-.259.82-.336 2.084c-.02.347-.033.68-.033.983c0 .303.012.636.033.983c.077 1.264.118 1.68.336 2.084c.209.389.635.79 1.036.975c.414.192.901.208 2.345.208h.06c.455 0 .77 0 1.081.052c.307.052.605.142.89.267c.288.128.552.301.932.552l.05.033l.218.144c1.29.85 2.195 1.445 2.91 1.792c.711.346 1.068.36 1.317.275c.137-.047.272-.115.39-.199c.217-.152.415-.447.553-1.218c.14-.775.191-1.847.263-3.377c.052-1.12.086-2.06.086-2.571c0-.512-.034-1.451-.086-2.57c-.072-1.531-.123-2.603-.262-3.378c-.14-.771-.337-1.066-.553-1.218a1.569 1.569 0 0 0-.391-.2c-.25-.084-.606-.07-1.318.276Zm-.655-1.35c.826-.4 1.64-.624 2.457-.345c.272.093.534.226.769.391c.706.497 1.005 1.28 1.167 2.18c.159.884.213 2.056.281 3.516l.003.058c.052 1.115.088 2.088.088 2.64s-.036 1.525-.088 2.64l-.003.058c-.068 1.46-.122 2.632-.281 3.516c-.162.9-.461 1.683-1.167 2.18a3.066 3.066 0 0 1-.769.39c-.818.28-1.631.057-2.457-.345c-.814-.395-1.8-1.046-3.032-1.857l-.267-.176c-.447-.295-.602-.394-.76-.464a2.17 2.17 0 0 0-.535-.16c-.171-.03-.354-.032-.89-.032h-.162c-1.217 0-2.062.001-2.814-.347a3.962 3.962 0 0 1-1.727-1.624c-.392-.729-.438-1.49-.504-2.575l-.008-.13A17.876 17.876 0 0 1 .75 12c0-.341.014-.706.036-1.074l.008-.13C.86 9.712.906 8.95 1.298 8.22a3.962 3.962 0 0 1 1.727-1.624c.752-.348 1.597-.348 2.814-.347H6c.537 0 .72-.002.891-.031a2.25 2.25 0 0 0 .534-.16c.16-.07.314-.17.761-.465l.267-.176c1.231-.811 2.218-1.462 3.032-1.857Zm8.035 1.864a.75.75 0 0 1 1.056.096L20 6l.576-.48l.001.001l.002.002l.003.004l.007.009a2.395 2.395 0 0 1 .086.114a4.7 4.7 0 0 1 .203.311c.161.27.368.665.572 1.195c.408 1.061.8 2.653.8 4.844c0 2.192-.392 3.783-.8 4.845a7.7 7.7 0 0 1-.572 1.194a4.677 4.677 0 0 1-.268.399l-.013.017l-.008.01l-.007.009l-.003.003l-.002.002L20 18l.576.48a.75.75 0 0 1-1.156-.955m0 0l.003-.005l.031-.041a3.28 3.28 0 0 0 .137-.212c.12-.198.288-.516.459-.961c.342-.889.7-2.297.7-4.306c0-2.008-.358-3.417-.7-4.306a6.212 6.212 0 0 0-.459-.961a3.274 3.274 0 0 0-.171-.257l.003.004l-.001-.002l-.002-.002a.75.75 0 0 1 .1-1.052m-1.884 2.92a.75.75 0 0 1 1.02.292l-.628.349l.628-.349v.001l.001.002l.002.003l.004.007l.01.018l.026.054a5.001 5.001 0 0 1 .278.805c.14.551.273 1.364.273 2.474s-.132 1.923-.273 2.474a5.172 5.172 0 0 1-.203.631a2.947 2.947 0 0 1-.102.228l-.01.019l-.003.007l-.002.003v.001s-.001.002-.63-.347l.629.348a.75.75 0 0 1-1.317-.719l.005-.01a3.665 3.665 0 0 0 .18-.534c.108-.424.226-1.11.226-2.1s-.118-1.678-.227-2.102a3.662 3.662 0 0 0-.179-.534l-.005-.01a.75.75 0 0 1 .297-1.01Z"
                            clipRule="evenodd"
                        ></path>
                    ) : (
                        <path
                            fill="currentColor"
                            fillRule="evenodd"
                            d="M22.53 1.47a.75.75 0 0 1 0 1.06l-20 20a.75.75 0 0 1-1.06-1.06l3.721-3.722c-.883-.01-1.555-.062-2.166-.345a3.962 3.962 0 0 1-1.727-1.624c-.392-.729-.438-1.49-.504-2.575l-.008-.13A17.876 17.876 0 0 1 .75 12c0-.341.014-.706.036-1.074l.008-.13C.86 9.712.906 8.95 1.298 8.22a3.962 3.962 0 0 1 1.727-1.624c.752-.348 1.597-.348 2.814-.347H6c.537 0 .72-.002.891-.031a2.25 2.25 0 0 0 .534-.16c.16-.07.314-.17.761-.465l.267-.176c1.231-.811 2.218-1.462 3.032-1.857c.826-.402 1.64-.625 2.457-.346c.272.093.534.226.769.391c.64.45.944 1.135 1.116 1.924c.088.402.148.865.193 1.389l5.45-5.45a.75.75 0 0 1 1.06 0Zm-7.92 6.859c-.057-1.101-.121-1.894-.249-2.48c-.136-.624-.317-.878-.513-1.015a1.569 1.569 0 0 0-.39-.2c-.25-.084-.606-.07-1.318.276c-.714.347-1.62.942-2.909 1.792l-.219.144l-.05.033c-.38.25-.643.424-.931.552a3.75 3.75 0 0 1-.89.267c-.31.052-.626.052-1.082.052H6c-1.444 0-1.93.016-2.345.208a2.47 2.47 0 0 0-1.036.975c-.218.405-.259.82-.336 2.084c-.02.347-.033.68-.033.983c0 .303.012.636.033.983c.077 1.264.118 1.68.336 2.084c.209.389.635.79 1.036.975c.414.192.901.208 2.345.208h.06c.239 0 .44 0 .622.008l7.928-7.93Zm5.655-2.013a.75.75 0 0 1 .991.376c.468 1.035.994 2.768.994 5.308c0 2.192-.392 3.783-.8 4.844a7.72 7.72 0 0 1-.572 1.195a4.73 4.73 0 0 1-.289.425l-.007.01l-.003.003l-.002.002L20 18l.576.48a.75.75 0 0 1-1.156-.956l.003-.004l.031-.041a3.27 3.27 0 0 0 .137-.212c.12-.199.288-.516.459-.961c.342-.889.7-2.298.7-4.306c0-2.326-.48-3.849-.86-4.692a.75.75 0 0 1 .375-.992Zm-2.101 2.95a.75.75 0 0 1 .887.582c.11.53.199 1.24.199 2.152c0 1.11-.132 1.923-.273 2.474a5.172 5.172 0 0 1-.203.631a2.947 2.947 0 0 1-.102.228l-.01.018l-.003.007l-.002.003v.002s-.001.001-.63-.348l.629.35a.75.75 0 0 1-1.317-.72l.005-.01a1.85 1.85 0 0 0 .038-.087a3.66 3.66 0 0 0 .141-.447c.11-.424.227-1.111.227-2.101a9.17 9.17 0 0 0-.168-1.848a.75.75 0 0 1 .582-.886ZM15.5 11.25a.75.75 0 0 1 .75.75c0 .552-.036 1.525-.088 2.64l-.003.058c-.068 1.46-.122 2.632-.281 3.516c-.162.9-.461 1.683-1.167 2.18a3.066 3.066 0 0 1-.769.39c-.742.254-1.48.093-2.222-.235c-.732-.324-1.592-.868-2.631-1.549a.75.75 0 1 1 .822-1.255c1.059.694 1.815 1.166 2.416 1.432c.592.262.905.265 1.13.188c.138-.047.273-.115.391-.199c.217-.152.415-.447.553-1.218c.14-.775.191-1.847.263-3.377c.052-1.12.086-2.06.086-2.571a.75.75 0 0 1 .75-.75Z"
                            clipRule="evenodd"
                        ></path>
                    )}
                </svg>
            </div>

            {/* Pause */}
            <div
                className="rounded-full bg-white bg-opacity-50 p-3 cursor-pointer hover:bg-opacity-100 group duration-150"
                onClick={() => {
                    setVideoIsForcedPaused(!videoIsForcedPaused);
                    setHighlightedEpisodeIsPaused(!highlightedEpisodeIsPaused);
                    if (highlightEnd) {
                        setHighlightEnd(false);
                        restartVideo();
                    }
                }}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-white group-hover:text-gold duration-150"
                    viewBox="0 0 24 24"
                >
                    {highlightedEpisodeIsPaused ? (
                        <path
                            fill="currentColor"
                            d="M21.409 9.353a2.998 2.998 0 0 1 0 5.294L8.597 21.614C6.534 22.736 4 21.276 4 18.968V5.033c0-2.31 2.534-3.769 4.597-2.648l12.812 6.968Z"
                        ></path>
                    ) : (
                        <path
                            fill="currentColor"
                            d="M2 6c0-1.886 0-2.828.586-3.414C3.172 2 4.114 2 6 2c1.886 0 2.828 0 3.414.586C10 3.172 10 4.114 10 6v12c0 1.886 0 2.828-.586 3.414C8.828 22 7.886 22 6 22c-1.886 0-2.828 0-3.414-.586C2 20.828 2 19.886 2 18V6Zm12 0c0-1.886 0-2.828.586-3.414C15.172 2 16.114 2 18 2c1.886 0 2.828 0 3.414.586C22 3.172 22 4.114 22 6v12c0 1.886 0 2.828-.586 3.414C20.828 22 19.886 22 18 22c-1.886 0-2.828 0-3.414-.586C14 20.828 14 19.886 14 18V6Z"
                        ></path>
                    )}
                </svg>
            </div>

            {/* Reload the video */}
            <div
                className="rounded-full bg-white bg-opacity-50 p-3 cursor-pointer hover:bg-opacity-100 group duration-150"
                onClick={() => {
                    restartVideo();
                    setHighlightedEpisodeIsPaused(false);
                    setVideoIsForcedPaused(false);
                    setHighlightedEpisodeIsMuted(false);
                    setHighlightEnd(false);
                }}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-white group-hover:text-gold duration-150"
                    viewBox="0 0 24 24"
                >
                    <path
                        fill="currentColor"
                        fillRule="evenodd"
                        d="M2.93 11.2c.072-4.96 4.146-8.95 9.149-8.95a9.158 9.158 0 0 1 7.814 4.357a.75.75 0 0 1-1.277.786a7.658 7.658 0 0 0-6.537-3.643c-4.185 0-7.575 3.328-7.648 7.448l.4-.397a.75.75 0 0 1 1.057 1.065l-1.68 1.666a.75.75 0 0 1-1.056 0l-1.68-1.666A.75.75 0 1 1 2.528 10.8l.403.4Zm16.856-.733a.75.75 0 0 1 1.055 0l1.686 1.666a.75.75 0 1 1-1.054 1.067l-.41-.405c-.07 4.965-4.161 8.955-9.18 8.955a9.197 9.197 0 0 1-7.842-4.356a.75.75 0 1 1 1.277-.788a7.697 7.697 0 0 0 6.565 3.644c4.206 0 7.61-3.333 7.68-7.453l-.408.403a.75.75 0 1 1-1.055-1.067l1.686-1.666Z"
                        clipRule="evenodd"
                    ></path>
                </svg>
            </div>
        </>
    );

    let highlightVideo = (
        <>
            {highlight && (
                <Vimeo
                    video={getVimeoId(highlight.video)}
                    showByline={false}
                    color="8468e3"
                    showTitle={false}
                    showPortrait={false}
                    background={true}
                    speed
                    dnt
                    style={{
                        width: "100%",
                        height: "100%",
                        opacity: highlightEnd ? 0 : 1,
                    }}
                    loop={false}
                    responsive
                    ref={highlightedVideoRef}
                    muted={highlightedEpisodeIsMuted}
                    onEnd={() => {
                        setHighlightEnd(true);
                        setHighlightedEpisodeIsPaused(true);
                    }}
                    paused={highlightedEpisodeIsPaused}
                    autoplay={window.innerWidth > 768}
                    volume={highlightedEpisodeIsMuted ? 0 : 1}
                    quality="auto"
                    onReady={(e) => {
                        setVideo(e);
                        setVideoIsLoading(false);
                    }}
                    onLoaded={() => {
                        setVideoIsLoading(false);
                    }}
                />
            )}
        </>
    );

    return (
        <UserLayout currentPage="dashboard" withoutHeader>
            {/* Modals */}
            <div
                className={`top-0 left-0 bottom-0 right-0 w-full min-h-full overflow-y-auto transition-all duration-200 ease-in-out bg-black cursor-pointer ${
                    modalIsOpen
                        ? "bg-opacity-80 fixed"
                        : "bg-opacity-0 absolute"
                }`}
                style={{ zIndex: modalIsOpen ? 1000 : -100 }}
                onClick={() => setModalIsOpen(false)}
            >
                <div
                    className={`transition-all duration-200 transform lg:mx-0 mx-5 ${
                        modalIsOpen
                            ? "opacity-100 scale-100"
                            : "opacity-0 scale-50"
                    }`}
                >
                    {modal}
                </div>
            </div>

            {/* Header */}
            <NewHeader session={session} page="home" blackHeader />

            {/* Highlight for mobiles */}
            <div
                className="bg-light-100 dark:bg-bgDark-700 md:hidden block m-containerMobile"
                style={{
                    paddingTop: 100,
                }}
            >
                <div className="shadow-xl rounded-[17px] overflow-hidden">
                    <div className="aspect-w-16 aspect-h-9 relative w-full h-0 bg-black md:hidden block">
                        {highlight && (
                            <div
                                className="flex flex-col z-10 w-full top-0 left-0 bottom-0 right-0 absolute transition-all duration-500"
                                style={{
                                    background: `url('${
                                        highlight.storyArt
                                            ? highlight.storyArt
                                            : highlight.thumbnail
                                    }') lightgray 50% / cover no-repeat`,
                                }}
                            ></div>
                        )}
                        {/* Background with video */}
                        {/* {highlightVideo} */}
                        <div>
                            <div className="flex flex-col">
                                <div
                                    className="bottom-0 left-0 right-0 opacity-80 absolute top-0 z-30"
                                    style={{
                                        background:
                                            "linear-gradient(0deg,rgba(0,0,0),transparent 85%)",
                                        top: "10.09%",
                                    }}
                                ></div>
                                {/* Main title */}
                                {highlight && (
                                    <div className="flex-1 flex flex-col justify-end text-white z-30 absolute w-full h-full sm:pb-10 pb-4">
                                        <div className="w-full px-5">
                                            {highlight &&
                                            highlight.imageTitle ? (
                                                <Image
                                                    src={highlight.imageTitle}
                                                    alt="Logo"
                                                    width={625 * 0.4}
                                                    height={250 * 0.4}
                                                    className="object-contain mx-auto"
                                                />
                                            ) : (
                                                <div
                                                    className="2xl:text-5xl md:text-4xl sm:text-3xl text-xl bold text-center"
                                                    style={{
                                                        marginBottom: "1.2vw",
                                                        textShadow:
                                                            "2px 2px 4px rgba(0, 0, 0, .45)",
                                                    }}
                                                >
                                                    {highlight.title}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    {highlight && (
                        <div className="flex flex-col items-center justify-center gap-4 p-5 bg-gray-200 dark:bg-bgDark-840">
                            <div
                                className="lg:w-144 w-full lg:text-xl text-lg text-center dark:text-white"
                                style={{
                                    lineHeight: "135%",
                                }}
                            >
                                {highlight.description}
                            </div>
                            {highlight.link && (
                                <div className="flex items-center mt-1 gap-4">
                                    <Link href={highlight.link}>
                                        <Button type="white">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-5 w-5 mr-3 text-excitationOrange"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    fill="currentColor"
                                                    d="M21.409 9.353a2.998 2.998 0 0 1 0 5.294L8.597 21.614C6.534 22.736 4 21.276 4 18.968V5.033c0-2.31 2.534-3.769 4.597-2.648l12.812 6.968Z"
                                                ></path>
                                            </svg>
                                            Regarder
                                        </Button>
                                    </Link>
                                    {/* <div className="lg:hidden inline-flex items-center gap-3">
                                    {controls}
                                </div> */}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <div className="w-full z-10 bg-for-new dark:bg-for-newDark md:hidden block mt-4">
                <Slide
                    title="Nouveautés"
                    type="new"
                    news={episodesToHighlight}
                    highlightedEpisode={highlightedEpisode}
                    setHighlightedEpisode={onSetHighlightedEpisodeClicked}
                />
            </div>

            {/* Highlight for laptops */}
            <div className="aspect-w-16 aspect-h-9 relative w-full h-0 bg-black md:block hidden">
                <div
                    className="flex flex-col z-10 w-full top-0 left-0 bottom-0 right-0 absolute transition-all duration-500"
                    style={{
                        background:
                            // (!highlightEnd && !videoIsLoading) ||
                            // (highlight && !highlightEnd) ||
                            // !highlight
                            (highlight && !highlightEnd && !videoIsLoading) ||
                            !highlight
                                ? "transparent"
                                : `url('${
                                      highlight.storyArt
                                          ? highlight.storyArt
                                          : highlight.thumbnail
                                  }') lightgray 50% / cover no-repeat`,
                    }}
                ></div>
                {/* Background with video */} {highlightVideo}
                <div>
                    <div
                        className="bottom-0 left-0 opacity-75 absolute top-0 z-0"
                        style={{
                            background:
                                "linear-gradient(77deg,rgba(0,0,0,.6),transparent 85%)",
                            right: "10.09%",
                        }}
                    ></div>
                    <div className="z-30 h-full">
                        {/* Controls for video */}
                        <div
                            className="absolute z-30 lg:flex hidden flex-col justify-end gap-1"
                            style={{
                                right: "4%",
                                bottom: "35%",
                                top: "0",
                            }}
                        >
                            {controls}
                        </div>

                        {/* Main title */}
                        {highlight && (
                            <div
                                className="flex-1 flex flex-col justify-end text-white z-30 absolute"
                                style={{
                                    left: "4%",
                                    bottom: "35%",
                                    top: "0",
                                    width: "36%",
                                }}
                            >
                                <div className="z-30">
                                    {highlight && highlight.imageTitle ? (
                                        <Image
                                            src={highlight.imageTitle}
                                            alt="Logo"
                                            width={625 * 0.8}
                                            height={250 * 0.8}
                                            className="object-contain"
                                        />
                                    ) : (
                                        <div
                                            className="bold"
                                            style={{
                                                marginBottom: "1.2vw",
                                                textShadow:
                                                    "2px 2px 4px rgba(0, 0, 0, .45)",
                                                fontSize: "2.5vw",
                                            }}
                                        >
                                            {highlight.title}
                                        </div>
                                    )}
                                    <div
                                        className="lg:w-144 w-full mt-3"
                                        style={{
                                            lineHeight: "135%",
                                            textShadow:
                                                "2px 2px 4px rgba(0, 0, 0, .45)",
                                            fontSize: "1.2vw",
                                        }}
                                    >
                                        {highlight.description}
                                    </div>
                                    {highlight.link && (
                                        <div className="flex items-center gap-4 mt-5">
                                            <Link href={highlight.link}>
                                                <Button type="white">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="h-5 w-5 mr-3 text-excitationOrange"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            fill="currentColor"
                                                            d="M21.409 9.353a2.998 2.998 0 0 1 0 5.294L8.597 21.614C6.534 22.736 4 21.276 4 18.968V5.033c0-2.31 2.534-3.769 4.597-2.648l12.812 6.968Z"
                                                        ></path>
                                                    </svg>
                                                    Regarder
                                                </Button>
                                            </Link>
                                            <div className="lg:hidden inline-flex items-center gap-3">
                                                {controls}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        <div className="w-full z-10 bg-for-new dark:bg-for-newDark lg:bottom-0 -bottom-20 absolute">
                            <Slide
                                title="Nouveautés"
                                type="new"
                                news={episodesToHighlight}
                                highlightedEpisode={highlightedEpisode}
                                setHighlightedEpisode={
                                    onSetHighlightedEpisodeClicked
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Events and calls */}
            {allEventsPlanned.length > 0 && (
                <div className="lg:mt-0 mt-24">
                    <Slide
                        title="Vos rendez-vous"
                        type="events"
                        events={allEventsPlanned}
                        session={session}
                    />
                </div>
            )}

            {/* Courses */}
            <Slide
                title="Dernières formations"
                type="courses"
                lastCourses={lastCourses}
            />

            {/* Progressions */}
            {allCourses.length > 0 ? (
                <Slide
                    title="Reprenez là où vous en étiez"
                    type="progressions"
                    courses={allCourses}
                />
            ) : (
                <div></div>
            )}

            {/* Articles */}
            <Slide
                title="Derniers articles"
                type="articles"
                articles={articles}
                session={session}
            />

            {/* Believemy Originals Series */}
            <Slide
                title="Séries Believemy Originals"
                type="series"
                series={series}
                setModal={setModal}
                modalIsOpen={modalIsOpen}
                setModalIsOpen={setModalIsOpen}
            />

            {/* Mentoring */}
            <Slide
                title="Faites-vous accompagner individuellement"
                type="mentors"
                mentors={mentors}
            />

            {/* Foot */}
            <Footer />
        </UserLayout>
    );
}
